import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Divider,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';
import DialogBasic from './DialogBasic';
import { useNewContractGuideChecklist } from '../../hooks/useNewContractGuideChecklist';

const ACCENT = '#137fec';
const ACCENT_DEEP = '#0f6dd1';
const BORDER = 'rgba(226, 232, 240, 1)';

function Section({ number, title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: 2.5,
        overflow: 'hidden',
        bgcolor: '#fff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 0 }}>
        <Box
          sx={{
            width: 5,
            flexShrink: 0,
            background: `linear-gradient(180deg, ${ACCENT} 0%, ${ACCENT_DEEP} 100%)`,
          }}
        />
        <Stack spacing={1.35} sx={{ p: 2.25, flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                minWidth: 34,
                height: 34,
                borderRadius: 1.25,
                bgcolor: alpha(ACCENT, 0.12),
                color: ACCENT,
                display: 'grid',
                placeItems: 'center',
                fontWeight: 900,
                fontSize: '0.8125rem',
                letterSpacing: '-0.02em',
              }}
            >
              {number}
            </Box>
            <Typography
              component="h3"
              sx={{
                fontWeight: 800,
                fontSize: '1.02rem',
                letterSpacing: '-0.02em',
                color: 'text.primary',
              }}
            >
              {title}
            </Typography>
          </Stack>
          <Box sx={{ pl: { xs: 0, sm: 0.25 } }}>{children}</Box>
        </Stack>
      </Box>
    </Paper>
  );
}

function Body({ children, muted = false, sx: sxProp = {} }) {
  return (
    <Typography
      component="div"
      variant="body2"
      sx={{
        color: muted ? 'text.secondary' : 'text.primary',
        lineHeight: 1.75,
        fontSize: '0.9375rem',
        letterSpacing: '-0.01em',
        ...sxProp,
      }}
    >
      {children}
    </Typography>
  );
}

function SubBlock({ code, title, children }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(ACCENT, 0.04),
        border: `1px dashed ${alpha(ACCENT, 0.28)}`,
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '0.875rem',
          color: ACCENT_DEEP,
          letterSpacing: '-0.01em',
        }}
      >
        {code} {title}
      </Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Box>
  );
}

function BulletList({ items }) {
  return (
    <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.25, py: 0.5 }}>
      {items.map((t) => (
        <Typography
          key={t}
          component="li"
          sx={{
            fontSize: '0.9375rem',
            lineHeight: 1.65,
            color: 'text.primary',
            '&::marker': { color: ACCENT },
          }}
        >
          {t}
        </Typography>
      ))}
    </Stack>
  );
}

/** 5. 최종 확인 — 기본정보 4항목 (조회 API 결과로 완료 여부 판단) */
const FINAL_CHECKLIST_ROWS = [
  {
    area: '사업장',
    setupKey: 'workplaceRegistered',
    navigateTo: '/Plant',
  },
  {
    area: '인력',
    setupKey: 'workforceRegistered',
    navigateTo: '/Member',
  },
  {
    area: '부서',
    setupKey: 'departmentRegistered',
    navigateTo: '/Department',
  },
  {
    area: '공정',
    setupKey: 'processRegistered',
    navigateTo: '/Gongjong',
  },
];

function NewContractGuideDialog({
  open = false,
  onClose = () => {},
  onSave = () => {},
  dontShowToday = false,
  onToggleDontShowToday = () => {},
}) {
  const navigate = useNavigate();
  const {
    checklist,
    enabled: checklistEnabled,
    isLoading,
    isError,
  } = useNewContractGuideChecklist(open);

  const HandleGoMenu = (path) => {
    onClose?.();
    navigate(path);
  };

  return (
    <DialogBasic
      title={'신규 계약 운영 가이드 (시스템·고객사 관리자 공통)'}
      titleSx={{
        fontWeight: 800,
        fontSize: '1.2rem',
        letterSpacing: '-0.03em',
        pb: 2,
        pt: 1,
        px: 0.5,
        borderBottom: `1px solid ${BORDER}`,
        background: `linear-gradient(125deg, ${alpha(ACCENT, 0.08)} 0%, #fff 55%, #fff 100%)`,
      }}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      saveButtonName={'확인'}
      closeButtonName={'닫기'}
      onSave={onSave}
      actions={
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={dontShowToday}
              onChange={(e) => onToggleDontShowToday(e.target.checked)}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              하루 동안 보지 않기
            </Typography>
          }
          sx={{ ml: 0.25 }}
        />
      }
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${BORDER}`,
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.14)',
        },
      }}
      contentSx={{
        p: 0,
        pt: 4,
        px: 2.5,
        pb: 2,
        maxHeight: '72vh',
        overflowY: 'auto',
        bgcolor: 'rgba(248, 250, 252, 0.65)',
      }}
      content={
        <Stack spacing={2.25}>
          <Paper
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: 2.5,
              border: `1px solid ${BORDER}`,
              background:
                'linear-gradient(135deg, rgba(19,127,236,0.07) 0%, #fff 48%, #fff 100%)',
            }}
          >
            <Stack direction="row" spacing={1.75} alignItems="flex-start">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(ACCENT, 0.12),
                  color: ACCENT,
                  border: `1px solid ${alpha(ACCENT, 0.2)}`,
                }}
              >
                <MenuBookOutlined sx={{ fontSize: 28 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1rem',
                    letterSpacing: '-0.02em',
                    mb: 0.75,
                  }}
                >
                  시스템·고객사 관리자 공통 참고
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    lineHeight: 1.65,
                  }}
                >
                  시스템 운영(개발·운영) 담당과 각 고객사(회사) 관리자 모두가 새
                  계약 등록 이후 필수 기본정보 4항목을 기준으로 점검할 수 있도록
                  정리한 문서입니다.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Section number="1" title="목적">
            <Body muted>
              본 가이드는 새 계약 등록 이후 시스템 운영을 위해 필수적으로
              설정하여야 하는 기본 항목 및 추가 설정 항목을 안내하는 데 목적이
              있습니다. 시스템·고객사 관리자는 본 가이드에 따라 계약별 초기
              정보를 정확히 등록하여 주요 기능이 정상적으로 수행될 수 있도록
              하여야 합니다.
            </Body>
          </Section>

          <Section number="2" title="기본정보 설정 항목 및 절차">
            <Body sx={{ mb: 1.5 }}>
              새 계약 등록 후 아래 네 항목을 순서대로 등록하면 기본정보 세팅이
              완료된 것으로 봅니다. 각 항목은 계약 운영의 기초 데이터이므로
              반드시 선행 등록하여야 합니다.
            </Body>
            <Stack spacing={1.25}>
              <Body>
                <strong>1. 사업장 등록</strong> — 해당 계약의 사업장 정보를 가장
                먼저 정확하게 입력합니다.
              </Body>
              <Body>
                <strong>2. 인력 등록</strong> — 조직 운영에 필요한 인력 정보를
                누락 없이 등록합니다.
              </Body>
              <Body>
                <strong>3. 부서 등록</strong> — 실제 운영 체계에 맞는 부서
                정보를 등록합니다.
              </Body>
              <Body>
                <strong>4. 공정 등록</strong> — 현장 수행 공정 정보를 업무
                절차에 맞게 등록합니다.
              </Body>
            </Stack>
          </Section>

          <Section number="3" title="등록 상태 점검">
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: 'text.secondary',
                mb: 1.25,
              }}
            >
              현재 선택된 회사·사업장 기준으로 기본정보 4항목의 등록 상태를 자동
              점검합니다. 완료는 초록 체크, 미완료는 빨간 점으로 표시되며,
              미완료 항목은 바로 해당 메뉴로 이동할 수 있습니다.
            </Typography>
            {!checklistEnabled && (
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'warning.dark',
                  mb: 1,
                  lineHeight: 1.6,
                }}
              >
                상단 조회조건에서 회사(관리자)·사업장을 선택한 뒤 다시 열면
                자동으로 점검됩니다.
              </Typography>
            )}
            {isLoading && (
              <LinearProgress
                sx={{ mb: 1, borderRadius: 1, height: 4 }}
                aria-label="등록 여부 조회 중"
              />
            )}
            {isError && (
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'error.main',
                  mb: 1,
                }}
              >
                일부 조회에 실패했습니다. 네트워크 확인 후 가이드를 닫았다가
                다시 열어 주세요.
              </Typography>
            )}
            <List
              dense
              disablePadding
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {FINAL_CHECKLIST_ROWS.map((row, idx) => {
                const done = !!checklist[row.setupKey];
                const rowLabel = done
                  ? `${row.area} 등록 완료`
                  : `${row.area} 등록 하러가기`;
                return (
                  <ListItem
                    key={row.setupKey}
                    alignItems="flex-start"
                    sx={{
                      py: 1.35,
                      px: 1.75,
                      borderRadius: 2,
                      bgcolor: done ? alpha('#2e7d32', 0.06) : '#fff8e1',
                      border: `1px solid ${BORDER}`,
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 26,
                        height: 26,
                        borderRadius: 1,
                        bgcolor: alpha(ACCENT, 0.1),
                        color: ACCENT,
                        fontWeight: 900,
                        fontSize: '0.75rem',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.25 }}>
                      {done ? (
                        <CheckCircleIcon
                          sx={{ color: '#2e7d32', fontSize: 28 }}
                        />
                      ) : (
                        <FiberManualRecordIcon
                          sx={{ color: '#c62828', fontSize: 24 }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={rowLabel}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          lineHeight: 1.45,
                        },
                      }}
                      sx={{ flex: 1, minWidth: 0, mr: { sm: 1 } }}
                    />
                    {!done && (
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={() => HandleGoMenu(row.navigateTo)}
                        sx={{
                          minHeight: 40,
                          flexShrink: 0,
                          fontWeight: 700,
                          alignSelf: { xs: 'flex-end', sm: 'center' },
                        }}
                      >
                        [이동]
                      </Button>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Section>

          <Section
            number="4"
            title="TBM (Tool Box Meeting) 진행을 위한 웹 사전 등록사항"
          >
            <Body sx={{ mb: 1.75 }}>
              TBM은 현장에서 모바일 앱을 통해 진행·이용하는 기능입니다. 관리자는
              TBM 진행 전 웹에서 참고자료 등록 상태를 점검하여야 합니다.
              <br />
              교육동영상·교육파일·작업계획서가 없어도 TBM 진행은 가능하나, 이
              경우 추가회의를 필수로 진행하여야 합니다.
            </Body>
            <Stack spacing={1.75}>
              <SubBlock
                code="4.1"
                title="교육 및 훈련 메뉴 > TBM 메뉴 > 교육자료 탭"
              >
                <Body>
                  교육 및 훈련 메뉴의 TBM 메뉴 내 교육자료 탭에서는 TBM 진행 시
                  활용할 교육자료가 조회됩니다. 따라서 관리자는 다음 자료를
                  사전에 등록하여야 합니다.
                </Body>
                <BulletList
                  items={[
                    '교육동영상',
                    '교육파일',
                    '기타 해당 부서에 등록된 교육자료',
                  ]}
                />
                <Typography
                  sx={{
                    mt: 1.25,
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    lineHeight: 1.65,
                  }}
                >
                  교육자료 탭에서는 해당 부서에 등록된 모든 자료가 조회 대상이
                  되므로, 실제 운영 부서 기준으로 필요한 자료가 누락 없이
                  등록되어 있는지 확인하여야 합니다.
                </Typography>
              </SubBlock>
              <SubBlock code="4.2" title="안전작업관리 메뉴 > 작업계획서">
                <Body>
                  안전작업관리 메뉴의 작업계획서에는 최근 3년 동안 등록된 자료가
                  등록 및 관리되어 있어야 합니다. 관리자는 TBM 진행 시 참고 또는
                  활용이 필요한 작업계획서가 최근 3년 기준으로 정상 등록되어
                  있는지 반드시 점검하여야 합니다.
                </Body>
              </SubBlock>
              <SubBlock code="4.3" title="파일 미등록 시 필수 절차">
                <Body sx={{ mb: 1 }}>
                  <strong>
                    교육동영상·교육파일·작업계획서가 모두 미등록이어도
                  </strong>{' '}
                  TBM 진행은 가능합니다.
                </Body>
                <Box
                  sx={{
                    mt: 1.25,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha('#f59e0b', 0.45)}`,
                    bgcolor: alpha('#f59e0b', 0.1),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      color: '#b71c1c',
                      lineHeight: 1.65,
                      fontWeight: 800,
                    }}
                  >
                    필수: 위 세 항목이 모두 미등록인 경우, TBM 시작 전
                    추가회의를 반드시 진행해야 합니다.
                  </Typography>
                </Box>
              </SubBlock>
            </Stack>
          </Section>

          <Divider sx={{ borderColor: BORDER, my: 0.5 }} />
          <Typography
            align="center"
            sx={{
              fontSize: '0.75rem',
              color: 'text.disabled',
              pb: 0.5,
              letterSpacing: '0.02em',
            }}
          >
            관리자 공통 참고 자료 · 절차·용어 변경 시 본 문서를 함께 갱신해
            주세요
          </Typography>
        </Stack>
      }
    />
  );
}

export default React.memo(NewContractGuideDialog);
